import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useOnboarding } from '../components/OnboardingTour';
import { IconCheck, IconProfile, IconWarning, IconStar } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import { Link } from 'react-router-dom';
import { usePlan } from '../lib/PlanContext';
import { LEGAL_LINKS } from '../lib/legalContent';
import { PRO_PRICE_LABEL } from '../lib/plans';

export default function Profile() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { reset: resetOnboarding } = useOnboarding();
  const { isPro, isLifetime, subscription, startCheckout, openPortal, redirecting } = usePlan();

  const handleTourReset = () => {
    resetOnboarding();
    navigate('/');
  };
  const [form, setForm] = useState({
    name: '',
    invoice_prefix: 'INV',
    bank_info: '',
    qualified_invoice_no: '',
    signature: '',
  });
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasQualified, setHasQualified] = useState(false); // 適格番号あり/なし

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) return;
    setEmail(user.email || '');
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    if (data) {
      setForm({
        name: data.name || '',
        invoice_prefix: data.invoice_prefix || 'INV',
        bank_info: data.bank_info || '',
        qualified_invoice_no: data.qualified_invoice_no || '',
        signature: data.signature || '',
      });
      setHasQualified(!!data.qualified_invoice_no);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) {
      setSaving(false);
      alert('ログイン状態を確認できませんでした。ページを再読み込みしてもう一度お試しください。');
      return;
    }
    const { error } = await supabase.from('profiles').update({
      name: form.name,
      invoice_prefix: form.invoice_prefix,
      bank_info: form.bank_info,
      qualified_invoice_no: hasQualified ? form.qualified_invoice_no : '',
      signature: form.signature,
    }).eq('id', user.id);
    setSaving(false);
    if (error) {
      console.error('profile update error:', error);
      alert('保存に失敗しました: ' + error.message);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleSignOut = async () => {
    if (!window.confirm('ログアウトしますか？')) return;
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const s = {
    card: { background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 14, padding: '20px' },
    label: { fontSize: 12, color: theme.textSecondary, marginBottom: 6, display: 'block' },
    input: { width: '100%', background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '10px 14px', fontSize: 14, color: theme.textPrimary, outline: 'none', boxSizing: 'border-box' },
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <p style={{ color: theme.textMuted }}>読み込み中...</p>
    </div>
  );

  return (
    <div style={{ padding: '20px 16px', maxWidth: 520, margin: '0 auto' }}>

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, margin: 0 }}><IconProfile size={18}/> プロフィール設定</h1>
        <p style={{ fontSize: 13, color: theme.textMuted, margin: '4px 0 0' }}>表示名や請求書情報を設定</p>
      </div>

      {/* プラン */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>プラン</div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 180, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: theme.textPrimary, letterSpacing: '-0.01em' }}>
                {isPro ? 'Pro' : 'Free'}
              </span>
              {isLifetime && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 999, background: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.accent }}>
                  <IconStar size={11} color={theme.accent} /> 初期メンバー特典
                </span>
              )}
            </div>

            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 6, lineHeight: 1.7 }}>
              {isLifetime
                ? 'Totonoeeを早くから使ってくださっている方への特典として、すべての機能を永久にご利用いただけます。お支払いは発生しません。'
                : isPro
                  ? (subscription?.cancel_at_period_end && subscription?.current_period_end
                      ? `${new Date(subscription.current_period_end).toLocaleDateString('ja-JP')} に解約されます`
                      : '案件は無制限。見積書兼注文書・稼働証明書も出力できます。')
                  : '案件は10件まで。見積書兼注文書と稼働証明書は出力できません。'}
            </div>
          </div>

          {/* 永久Proの方にはボタンを出さない（Stripe顧客が存在しないため） */}
          {isLifetime ? null : isPro ? (
            <button onClick={openPortal} disabled={redirecting}
              style={{ padding: '11px 16px', borderRadius: 10, border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textPrimary, whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, cursor: redirecting ? 'default' : 'pointer' }}>
              {redirecting ? '開いています...' : 'お支払い管理'}
            </button>
          ) : (
            <button onClick={startCheckout} disabled={redirecting}
              style={{ padding: '11px 16px', borderRadius: 10, border: 'none', background: theme.primary, color: theme.primaryText, whiteSpace: 'nowrap', fontSize: 13, fontWeight: 700, cursor: redirecting ? 'default' : 'pointer' }}>
              {redirecting ? '開いています...' : `Proにする（${PRO_PRICE_LABEL}）`}
            </button>
          )}
        </div>
      </div>

      {/* アカウント情報 */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>アカウント</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentSub})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {form.name ? form.name.charAt(0).toUpperCase() : email.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: theme.textPrimary }}>{form.name || 'お名前未設定'}</div>
            <div style={{ fontSize: 13, color: theme.textMuted }}>{email}</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>表示名 <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="text" placeholder="例：田中 太郎" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} style={s.input} />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>ダッシュボードの挨拶に表示されます</div>
          </div>
          <div>
            <label style={s.label}>メールアドレス</label>
            <input type="text" value={email} disabled style={{ ...s.input, color: theme.textMuted, cursor: 'not-allowed' }} />
          </div>
        </div>
      </div>

      {/* 請求書設定 */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>請求書設定</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>請求書番号プレフィックス</label>
            <input type="text" placeholder="INV" value={form.invoice_prefix}
              onChange={e => setForm({ ...form, invoice_prefix: e.target.value })} style={s.input} />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
              例：「{form.invoice_prefix || 'INV'}」→ {form.invoice_prefix || 'INV'}-2026-001
            </div>
          </div>

          <div>
            <label style={s.label}>振込先情報</label>
            <textarea rows={3}
              placeholder={'例：\n〇〇銀行 〇〇支店\n普通口座 1234567\n口座名義：タナカ タロウ'}
              value={form.bank_info}
              onChange={e => setForm({ ...form, bank_info: e.target.value })}
              style={{ ...s.input, resize: 'vertical', lineHeight: 1.6 }} />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>請求書に自動で記載されます</div>
          </div>
        </div>
      </div>

      {/* ★ 適格請求書発行事業者番号 */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>インボイス設定</div>
        <div style={{ fontSize: 11, color: theme.textWeak, marginBottom: 16 }}>適格請求書発行事業者の登録番号</div>

        {/* あり/なし トグル */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[{ label: '登録なし', val: false }, { label: '登録あり', val: true }].map(opt => (
            <button key={String(opt.val)} onClick={() => setHasQualified(opt.val)}
              style={{
                flex: 1, padding: '9px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: hasQualified === opt.val ? theme.primary : theme.bgInput,
                color: hasQualified === opt.val ? theme.primaryText : theme.textMuted,
                outline: hasQualified === opt.val ? 'none' : `1px solid ${theme.border}`,
              }}>
              {opt.label}
            </button>
          ))}
        </div>

        {hasQualified && (
          <div>
            <label style={s.label}>登録番号</label>
            <input
              type="text"
              placeholder="T1234567890123"
              value={form.qualified_invoice_no}
              onChange={e => setForm({ ...form, qualified_invoice_no: e.target.value })}
              style={s.input}
            />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
              「T」＋13桁の数字（例：T1234567890123）
            </div>
            {form.qualified_invoice_no && !form.qualified_invoice_no.match(/^T\d{13}$/) && (
              <div style={{ fontSize: 11, color: '#f97316', marginTop: 4 }}>
                <IconWarning size={12}/> 形式が正しくない可能性があります（T＋13桁）
              </div>
            )}
          </div>
        )}

        {!hasQualified && (
          <div style={{ background: theme.bgInput, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: theme.textWeak }}>
            登録なしの場合、PDFに登録番号は記載されません
          </div>
        )}
      </div>

      {/* 署名欄 */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>署名・事業者情報</div>
        <div style={{ fontSize: 11, color: theme.textWeak, marginBottom: 16 }}>請求書の署名欄に表示されます</div>
        <div>
          <label style={s.label}>署名内容</label>
          <textarea rows={4}
            placeholder={'例：\n屋号：〇〇デザイン事務所\n氏名：田中 太郎\n住所：東京都〇〇区〇〇1-2-3\nTEL：090-0000-0000'}
            value={form.signature}
            onChange={e => setForm({ ...form, signature: e.target.value })}
            style={{ ...s.input, resize: 'vertical', lineHeight: 1.6 }} />
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>請求書PDFの右下に署名として記載されます</div>
        </div>
      </div>

      {/* 保存ボタン */}
      <button onClick={handleSave} disabled={saving || !form.name.trim()}
        style={{
          width: '100%', padding: '14px', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 600,
          cursor: form.name.trim() ? 'pointer' : 'default', marginBottom: 12,
          background: saved ? theme.badgeBg6 : form.name.trim() ? theme.primary : theme.bgHover,
          color: saved ? theme.badgeTxt6 : form.name.trim() ? theme.primaryText : theme.textWeak,
          transition: 'all 0.2s',
        }}>
        {saved ? '保存しました！' : saving ? '保存中...' : '変更を保存する'}
      </button>

      <button onClick={handleTourReset}
        style={{ width: '100%', padding: '12px', borderRadius: 12, border: `1px solid ${theme.border}`, background: 'transparent', fontSize: 13, color: theme.textMuted, cursor: 'pointer', marginBottom: 8 }}>
        機能ツアーをもう一度見る
      </button>

      <button onClick={handleSignOut}
        style={{ width: '100%', padding: '12px', borderRadius: 12, border: `1px solid ${theme.border}`, background: 'transparent', fontSize: 14, color: theme.colorRed, cursor: 'pointer' }}>
        ログアウト
      </button>

      {/* 法務リンク */}
      <div style={{
        marginTop: 28, paddingTop: 18, borderTop: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px 16px',
      }}>
        {LEGAL_LINKS.map(l => (
          <Link key={l.key} to={l.path}
            style={{ fontSize: 12, color: theme.textWeak, textDecoration: 'underline', textUnderlineOffset: 3 }}>
            {l.label}
          </Link>
        ))}
      </div>
      <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: theme.textWeak }}>
        © {new Date().getFullYear()} Digital商店
      </div>

    </div>
  );
}
